// The currencies a visitor may read prices in. Prices are stored in XOF and
// charged in USD; this table only says how to print a XOF amount in another
// currency. The backend's table (GET /api/currency/rates) is the source of
// truth and replaces these defaults once it answers; the defaults keep the
// first paint honest when it has not yet.

export const CHARGE_CURRENCY = "USD";

// The card is charged in US dollars at Toukky's own fixed rate, the way the
// server works it out (modules/payments/pricing.charge_amount, XOF_PER_USD):
// rounded to the cent, never below Stripe's $0.50. A page that books and pays
// at once prices the Apple Pay / Google Pay sheet with it before the payment
// exists; it is never this table's display rate.
export const CHARGE_XOF_PER_USD = 605;
export const STRIPE_MINIMUM_USD = 0.5;
export function chargeUsd(totalXof) {
  const total = Number(totalXof);
  if (!Number.isFinite(total) || total <= 0) return null;
  return Math.max(Math.round((total * 100) / CHARGE_XOF_PER_USD) / 100, STRIPE_MINIMUM_USD);
}

// code -> { symbol, name, xof_per_unit }
export const DEFAULT_CURRENCIES = {
  XOF: { symbol: "FCFA", name: "Franc CFA (UEMOA)", xof_per_unit: 1 },
  USD: { symbol: "$", name: "US dollar", xof_per_unit: 605 },
  EUR: { symbol: "€", name: "Euro", xof_per_unit: 655.957 },
  GBP: { symbol: "£", name: "British pound", xof_per_unit: 770 },
  XAF: { symbol: "FCFA", name: "Franc CFA (CEMAC)", xof_per_unit: 1 },
  MAD: { symbol: "MAD", name: "Moroccan dirham", xof_per_unit: 61 },
  TND: { symbol: "TND", name: "Tunisian dinar", xof_per_unit: 195 },
  DZD: { symbol: "DZD", name: "Algerian dinar", xof_per_unit: 4.5 },
  EGP: { symbol: "EGP", name: "Egyptian pound", xof_per_unit: 12.5 },
  NGN: { symbol: "₦", name: "Nigerian naira", xof_per_unit: 0.4 },
  GHS: { symbol: "GH₵", name: "Ghanaian cedi", xof_per_unit: 40 },
  GNF: { symbol: "GNF", name: "Guinean franc", xof_per_unit: 0.07 },
  GMD: { symbol: "GMD", name: "Gambian dalasi", xof_per_unit: 8.6 },
  MRU: { symbol: "MRU", name: "Mauritanian ouguiya", xof_per_unit: 15.2 },
  CVE: { symbol: "CVE", name: "Cape Verdean escudo", xof_per_unit: 5.95 },
  KES: { symbol: "KSh", name: "Kenyan shilling", xof_per_unit: 4.7 },
  ZAR: { symbol: "R", name: "South African rand", xof_per_unit: 33 },
  CDF: { symbol: "CDF", name: "Congolese franc", xof_per_unit: 0.21 },
  CAD: { symbol: "CA$", name: "Canadian dollar", xof_per_unit: 440 },
  CHF: { symbol: "CHF", name: "Swiss franc", xof_per_unit: 690 },
  SEK: { symbol: "kr", name: "Swedish krona", xof_per_unit: 57 },
  NOK: { symbol: "kr", name: "Norwegian krone", xof_per_unit: 56 },
  DKK: { symbol: "kr", name: "Danish krone", xof_per_unit: 88 },
  PLN: { symbol: "zł", name: "Polish złoty", xof_per_unit: 152 },
  TRY: { symbol: "₺", name: "Turkish lira", xof_per_unit: 18 },
  AED: { symbol: "AED", name: "UAE dirham", xof_per_unit: 165 },
  SAR: { symbol: "SAR", name: "Saudi riyal", xof_per_unit: 161 },
  QAR: { symbol: "QAR", name: "Qatari riyal", xof_per_unit: 166 },
  INR: { symbol: "₹", name: "Indian rupee", xof_per_unit: 7.2 },
  CNY: { symbol: "CN¥", name: "Chinese yuan", xof_per_unit: 84 },
  JPY: { symbol: "¥", name: "Japanese yen", xof_per_unit: 4 },
  KRW: { symbol: "₩", name: "South Korean won", xof_per_unit: 0.44 },
  AUD: { symbol: "A$", name: "Australian dollar", xof_per_unit: 400 },
  BRL: { symbol: "R$", name: "Brazilian real", xof_per_unit: 110 },
  MXN: { symbol: "MX$", name: "Mexican peso", xof_per_unit: 33 },
};

// The three a West African traveller reaches for first sit on top; the rest
// follow by code.
export const FEATURED = ["XOF", "USD", "EUR"];

export function orderedCodes(table) {
  const rest = Object.keys(table).filter((c) => !FEATURED.includes(c)).sort();
  return [...FEATURED.filter((c) => table[c]), ...rest];
}

// Merge the backend's answer ({rates: {code: xofToUnit}, currencies: {code: {symbol, name}}})
// over the defaults. A rate is XOF -> unit; the table keeps XOF per unit.
export function mergeRates(table, payload) {
  if (!payload || !payload.rates) return table;
  const next = { ...table };
  for (const [code, rate] of Object.entries(payload.rates)) {
    if (!(Number(rate) > 0)) continue;
    const meta = payload.currencies?.[code] || {};
    next[code] = { symbol: meta.symbol || next[code]?.symbol || code, name: meta.name || next[code]?.name || code, xof_per_unit: 1 / Number(rate) };
  }
  return next;
}

// Whole units everywhere: "12 000 FCFA", "$20", "€18", "£16", "MAD 200".
// The debit itself is printed by the server in USD with cents, at payment.
export function formatAmount(xof, code, table, lang = "fr") {
  if (xof == null || Number.isNaN(Number(xof))) return "";
  const row = table[code] || table.XOF;
  const value = Math.round(Number(xof) / (row.xof_per_unit || 1));
  if (code === "XOF" || code === "XAF") return `${value.toLocaleString("fr-FR")} ${row.symbol}`;
  if (code === "USD") return `$${value.toLocaleString("en-US")}`;
  if (code === "EUR") return `€${value.toLocaleString("fr-FR")}`;
  const digits = value.toLocaleString(lang === "fr" ? "fr-FR" : "en-US");
  // A one-character sign hugs the number; a code reads better with a space.
  return row.symbol.length <= 2 && !/^[A-Z]{2,}$/.test(row.symbol) ? `${row.symbol}${digits}` : `${row.symbol} ${digits}`;
}
